import superAdminApi from "./superAdminApi";
import CommonService from "./common.service";
import SuperAdminStorageService from "./SuperAdminStorageService";
import { isLogisticsStaffUser } from "../utils/roleSwitcher";
import { getLogisticsDedicatedSubmittedRfqPath } from "../utils/logisticsRfqPostSubmitNavigate";

const isLogisticsManagerPortal = () =>
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/dashboard/logistics-manager");

export const extractApiMessage = (err, fallback = "Request failed") =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

export async function checkExhibitionLogisticsDuplicate(form) {
  const payload = {
    exhibitionName: form.exhibitionName,
    expoCode: form.expoCode,
    venueCountry: form.venueCountry,
    venueState: form.venueState,
    venueCity: form.venueCity,
    venueAddress: form.venueAddress,
    setupDate: form.setupDate,
    exhibitionStartDate: form.exhibitionStartDate,
    exhibitionEndDate: form.exhibitionEndDate,
  };
  const res = await CommonService.post(
    "/logistics/exhibition-logistics-rfq/check-duplicate",
    payload,
    { silent: true },
  );
  const data = res?.data?.data ?? res?.data ?? res;
  return data ?? { hasDuplicate: false, existingRfq: null };
}

export async function submitExhibitionLogisticsRfq(form, options = {}) {
  const { confirmDuplicateSubmit = false, clientRequestId = null } = options;
  const payload = {
    rfqReferenceNumber: form.rfqReferenceNumber,
    companyId: form.companyId ?? null,
    contactPerson: form.contactPerson,
    contactNumber: form.contactNumber,
    companyName: form.companyName,
    companyEmail: form.companyEmail,
    exhibitionName: form.exhibitionName,
    expoCode: form.expoCode,
    setupDate: form.setupDate,
    exhibitionStartDate: form.exhibitionStartDate,
    exhibitionEndDate: form.exhibitionEndDate,
    orgCountry: form.orgCountry,
    orgState: form.orgState,
    orgCity: form.orgCity,
    orgPincode: form.orgPincode,
    orgAddress: form.orgAddress,
    venueCountry: form.venueCountry,
    venueState: form.venueState,
    venueCity: form.venueCity,
    venuePincode: form.venuePincode,
    venueAddress: form.venueAddress,
    boothNumber: form.boothNumber,
    materialsDescription: form.materialsDescription,
    totalWeightKg: form.totalWeightKg,
    totalWeightUnit: form.totalWeightUnit || 'KG',
    numberOfPackages: form.numberOfPackages,
    dimensionLength: form.dimensionLength,
    dimensionWidth: form.dimensionWidth,
    dimensionHeight: form.dimensionHeight,
    dimensionUnit: form.dimensionUnit || 'CM',
    packagingType: form.packagingType,
    declaredValueUsd: form.declaredValueUsd,
    declaredValueCurrency: form.declaredValueCurrency || 'INR',
    logisticsMode: form.logisticsMode,
    urgencyLevel: form.urgencyLevel,
    specialHandling: form.specialHandling,
    returnToOrigin: form.returnToOrigin,
    additionalServices: form.additionalServices,
    confirmDuplicateSubmit: confirmDuplicateSubmit || undefined,
    clientRequestId: clientRequestId || undefined,
  };

  const res = await CommonService.post("/logistics/exhibition-logistics-rfq", payload);
  return res;
}

export async function fetchExhibitionLogisticsRfqs(params = {}) {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get("/logistics-manager/exhibition-logistics-rfqs", params, {
      silent: true,
    });
    return res?.data ?? { items: [], total: 0, page: 1, pageSize: 20 };
  }
  const res = await superAdminApi.get("/super-admin/logistics/exhibition-logistics-rfqs", {
    params,
  });
  return res.data?.data ?? { items: [], total: 0, page: 1, pageSize: 20 };
}

export async function fetchExhibitionLogisticsRfqStats(params = {}) {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get("/logistics-manager/exhibition-logistics-rfqs/stats", params, {
      silent: true,
    });
    return (
      res?.data ?? {
        total: 0,
        assigned: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        thisMonth: 0,
      }
    );
  }
  const res = await superAdminApi.get("/super-admin/logistics/exhibition-logistics-rfqs/stats", {
    params,
  });
  return res.data?.data ?? {
    total: 0,
    assigned: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    thisMonth: 0,
  };
}

export async function fetchExhibitionLogisticsRfqCities() {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get(
      "/logistics-manager/exhibition-logistics-rfqs/cities",
      {},
      { silent: true },
    );
    return res?.data ?? { cities: [] };
  }
  const res = await superAdminApi.get("/super-admin/logistics/exhibition-logistics-rfqs/cities");
  return res.data?.data ?? { cities: [] };
}

export async function fetchExhibitionLogisticsRfqById(id) {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get(
      `/logistics-manager/exhibition-logistics-rfqs/${id}`,
      {},
      { silent: true },
    );
    return res?.data ?? { rfq: null };
  }
  const res = await superAdminApi.get(`/super-admin/logistics/exhibition-logistics-rfqs/${id}`);
  return res.data?.data ?? { rfq: null };
}

export function exhibitionLogisticsSubmittedRfqPath(id, options = {}) {
  return getLogisticsDedicatedSubmittedRfqPath("exhibition-logistics", id, options);
}

/** Load RFQ for submitted detail page (buyer, logistics manager, or super admin). */
export async function fetchExhibitionLogisticsRfqForSubmittedView(id) {
  if (SuperAdminStorageService.isAuthenticated()) {
    const data = await fetchExhibitionLogisticsRfqById(id);
    return data?.rfq ?? null;
  }
  if (isLogisticsStaffUser()) {
    const res = await CommonService.get(
      `/logistics-manager/exhibition-logistics-rfqs/${id}`,
      {},
      { silent: true },
    );
    const body = res?.data ?? res;
    return body?.rfq ?? (body && typeof body === "object" ? body : null);
  }
  const res = await CommonService.get(`/logistics/exhibition-logistics-rfq/my/${id}`);
  const body = res?.data?.data ?? res?.data ?? res;
  const rfq = body?.rfq ?? body;
  return rfq && typeof rfq === "object" ? rfq : null;
}
